import { createAction, Middleware } from '@reduxjs/toolkit';
import { LevelType } from 'data/types';
import { AutoExtendedEdge } from 'utils/space-polygon';
import { setCanRedo, setCanUndo } from './structure-history-slice';
import {
  restoreStructure,
  StructureImageAssignmentType
} from './structure-slice';

const MAX_HISTORY_SIZE = 20;

const UNDOABLE_ACTION_TYPES = new Set([
  'structure/setStructure',
  'structure/updateStructureItem',
  'structure/updateStructureItemAttribute',
  'structure/deleteStructureItem',
  'structure/applyAttributesToEntireLevel',
  'structure/updateSpaceSizingAttributes',
  'structure/addElectricalPanel',
  'structure/updateElectricalPanel',
  'structure/deleteElectricalPanel',
  'structure/unplaceElectricalPanel',
  'structure/moveElectricalPanelToLevel',
  'structure/setLevelFootprint',
  'structure/updateStructureImageTransform',
  'structure/addAutoLayoutSpaceIds',
  'structure/removeAutoLayoutSpaceIds',
  'structure/setAutoLayoutSpaceIds'
]);

export const structureUndo = createAction('structureHistory/undo');
export const structureRedo = createAction('structureHistory/redo');
export const clearStructureHistory = createAction('structureHistory/clear');
export const beginUndoBatch = createAction('structureHistory/beginBatch');
export const endUndoBatch = createAction('structureHistory/endBatch');

type StructureSnapshot = {
  structure: LevelType[];
  spaceExtensionRecords: Record<string, AutoExtendedEdge[]>;
  structureImageAssignments: Record<
    number,
    StructureImageAssignmentType | null
  >;
  autoLayoutSpaceIds: string[];
};

const past: StructureSnapshot[] = [];
const future: StructureSnapshot[] = [];
let batchDepth = 0;
let hasCapturedInBatch = false;
let isRestoring = false;
let suppressDepth = 0;

export function runWithoutStructureHistory<T>(fn: () => T): T {
  suppressDepth += 1;
  try {
    return fn();
  } finally {
    suppressDepth -= 1;
  }
}

const syncHistoryState = (dispatch: any) => {
  dispatch(setCanUndo(past.length > 0));
  dispatch(setCanRedo(future.length > 0));
};

const cloneSnapshotFromState = (state: any): StructureSnapshot => ({
  structure: structuredClone(state.structure.structure) as LevelType[],
  spaceExtensionRecords: structuredClone(
    state.structure.spaceExtensionRecords
  ) as Record<string, AutoExtendedEdge[]>,
  structureImageAssignments: structuredClone(
    state.structure.structureImageAssignments
  ) as Record<number, StructureImageAssignmentType | null>,
  autoLayoutSpaceIds: [...(state.structure.autoLayoutSpaceIds ?? [])]
});

const captureSnapshot = (state: any) => {
  past.push(cloneSnapshotFromState(state));
  if (past.length > MAX_HISTORY_SIZE) {
    past.shift();
  }
  future.length = 0;
};

const handleUndo = (store: any) => {
  if (past.length === 0) return;
  future.push(cloneSnapshotFromState(store.getState()));
  const previous = past.pop()!;
  isRestoring = true;
  store.dispatch(restoreStructure(previous));
  isRestoring = false;
  syncHistoryState(store.dispatch);
};

const handleRedo = (store: any) => {
  if (future.length === 0) return;
  past.push(cloneSnapshotFromState(store.getState()));
  const next = future.pop()!;
  isRestoring = true;
  store.dispatch(restoreStructure(next));
  isRestoring = false;
  syncHistoryState(store.dispatch);
};

const handleClear = (dispatch: any) => {
  past.length = 0;
  future.length = 0;
  batchDepth = 0;
  hasCapturedInBatch = false;
  syncHistoryState(dispatch);
};

const handleBeginBatch = () => {
  batchDepth += 1;
  if (batchDepth === 1) {
    hasCapturedInBatch = false;
  }
};

const handleEndBatch = (dispatch: any) => {
  if (batchDepth > 0) {
    batchDepth -= 1;
    if (batchDepth === 0) {
      hasCapturedInBatch = false;
      syncHistoryState(dispatch);
    }
  }
};

const handleUndoableAction = (store: any) => {
  const shouldCapture = batchDepth === 0 || !hasCapturedInBatch;
  if (shouldCapture) {
    captureSnapshot(store.getState());
    if (batchDepth > 0) {
      hasCapturedInBatch = true;
    }
  }
};

export const structureHistoryMiddleware: Middleware =
  (store) => (next) => (action: any) => {
    if (action.type === structureUndo.type) {
      handleUndo(store);
      return;
    }
    if (action.type === structureRedo.type) {
      handleRedo(store);
      return;
    }
    if (action.type === clearStructureHistory.type) {
      handleClear(store.dispatch);
      return;
    }
    if (action.type === beginUndoBatch.type) {
      handleBeginBatch();
      return;
    }
    if (action.type === endUndoBatch.type) {
      handleEndBatch(store.dispatch);
      return;
    }

    const shouldTrackHistory =
      !isRestoring &&
      suppressDepth === 0 &&
      UNDOABLE_ACTION_TYPES.has(action.type);

    if (shouldTrackHistory) {
      handleUndoableAction(store);
      if (batchDepth === 0) {
        syncHistoryState(store.dispatch);
      }
    }

    // eslint-disable-next-line consistent-return
    return next(action);
  };
